import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from "@mui/material";

import { campaignAPI } from "api/Campaigns/CampaignAPI";
import AddressAutocomplete from "components/Base/AddressAutocomplete";

export interface FieldConfig {
  name: string;
  label: string;
  required?: boolean;
  multiline?: boolean;
  rows?: number;
  type?: "text" | "address_autocomplete";
}

export interface AddEntityPopupProps<T> {
  open: boolean;
  title: string;
  fields: FieldConfig[];
  onClose: () => void;
  onSaved?: (newRecord: T) => void;
  createFunction: (data: Record<string, any>) => Promise<T>;
}

const AddEntityPopup = <T,>({
  open,
  title,
  fields,
  onClose,
  onSaved,
  createFunction,
}: AddEntityPopupProps<T>) => {
  const [formValues, setFormValues] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // --- Campaign dropdown state ---
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // Fetch campaigns only if popup contains linked_campaign field
  useEffect(() => {
    const hasLinkedCampaign = fields.some((f) => f.name === "linked_campaign");
    if (!open || !hasLinkedCampaign) return;

    const loadCampaigns = async () => {
      setCampaignsLoading(true);
      try {
        const data = await campaignAPI.getAll();
        setCampaigns(data || []);
      } catch (err) {
        console.error("Failed to load campaigns", err);
      } finally {
        setCampaignsLoading(false);
      }
    };

    loadCampaigns();
  }, [open, fields]);

  const handleChange = (field: string, value: any) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    for (const f of fields) {
      if (f.required && !formValues[f.name]) {
        setError(`${f.label} is required`);
        return;
      }
    }

    // EXTRA: if this popup contains address_autocomplete, require place_id
    const hasAddressAutocomplete = fields.some((f) => f.type === "address_autocomplete");
    if (hasAddressAutocomplete && !formValues.place_id) {
      setError("Please select an address from the suggestions (don’t just type it).");
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const newRecord = await createFunction(formValues);
      setFormValues({});
      onClose();
      onSaved?.(newRecord);
    } catch (err: any) {
      console.error("Failed to save entity:", err.response?.data || err.message);
      setError("Failed to save. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const handleClose = () => {
    setFormValues({});
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {fields.map((f) => {
            // SPECIAL CASE: linked_campaign → dropdown selector
            if (f.name === "linked_campaign") {
              return (
                <FormControl fullWidth key={f.name}>
                  <InputLabel>{f.label}</InputLabel>
                  <Select
                    label={f.label}
                    value={formValues[f.name] ?? ""}
                    onChange={(e) => handleChange(f.name, e.target.value)}
                  >
                    {/* --- blank option --- 
                    <MenuItem value="">
                      <em>-</em>
                    </MenuItem>
                    */}

                    {campaignsLoading ? (
                      <MenuItem disabled>Loading...</MenuItem>
                    ) : (
                      campaigns.map((c) => (
                        <MenuItem key={c.id} value={c.id}>
                          {c.name}
                        </MenuItem>
                      ))
                    )}
                  </Select>
                </FormControl>
              );
            }

            // Default input fields
            if (f.type === "text" || !f.type) {
              return (
                <TextField
                  key={f.name}
                  label={f.label}
                  variant="outlined"
                  fullWidth
                  required={f.required}
                  multiline={f.multiline}
                  rows={f.rows}
                  value={formValues[f.name] || ""}
                  onChange={(e) => handleChange(f.name, e.target.value)}
                />
              );
            }
            // Address autocomplete field
            if (f.type === "address_autocomplete") {
              return (
                <AddressAutocomplete
                  key={f.name}
                  label={f.label}
                  required={f.required}
                  value={formValues[f.name] || ""}
                  onChange={(val) => handleChange(f.name, val)}
                  onAddressSelected={(addr) => {
                    setFormValues((prev) => ({
                      ...prev,
                      ...addr,
                    }));
                  }}
                />
              );
            }
          })}

          {error && (
            <Box color="error.main" fontSize={14}>
              {error}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>

        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddEntityPopup;
